import ipaddr from "ipaddr.js";
import { allowedCidrBlocks } from "./allowedCidrBlocks";

const BLOCKED_RANGES = new Set([
  "loopback",
  "private",
  "linkLocal",
  "uniqueLocal",
  "unspecified",
  "broadcast",
  "multicast",
  "carrierGradeNat",
  "rfc6145",
  "rfc6052",
  "6to4",
  "teredo",
]);

// ipaddr.js files these with the documentation TEST-NETs under `reserved`, so
// only the non-routable blocks are denied.
const RESERVED_V4_BLOCKS = [
  "192.0.0.0/24",
  "192.88.99.0/24",
  "198.18.0.0/15",
  "240.0.0.0/4",
].map((block) => ipaddr.parseCIDR(block));

function isBlockedAddress(address: string): boolean {
  // `process` unwraps IPv4-mapped IPv6 (`::ffff:169.254.169.254`) so the IPv4
  // ranges and allowlist apply to it, mirroring the pony guard.
  const addr = ipaddr.process(address);
  // The deployment allowlist takes precedence over every block, including
  // loopback/link-local/metadata (mirrors the pony guard's `is_hard_blocked`).
  if (
    allowedCidrBlocks().some(
      ([net, bits]) => addr.kind() === net.kind() && addr.match(net, bits)
    )
  ) {
    return false;
  }
  return (
    BLOCKED_RANGES.has(addr.range()) ||
    (addr.kind() === "ipv4" &&
      RESERVED_V4_BLOCKS.some(([net, bits]) => addr.match(net, bits)))
  );
}

// Synchronous shape check for contexts that can't do DNS (shared mutators run
// on the client too); the authoritative guard is the async `resolveExternalUrl`,
// which resolves DNS and pins the IP.
export function isPublicHttpsUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^\[|]$/g, "");
    if (parsed.protocol !== "https:" || host === "localhost") return false;
    return !ipaddr.isValid(host) || !isBlockedAddress(host);
  } catch {
    return false;
  }
}

export type ResolvedExternalUrl = {
  parsed: URL;
  addresses: Array<{ address: string; family: 4 | 6 }>;
};

export async function validateExternalUrl(url: string): Promise<string | null> {
  const result = await resolveExternalUrl(url);
  return "reason" in result ? result.reason : null;
}

export async function resolveExternalUrl(
  url: string
): Promise<ResolvedExternalUrl | { reason: string }> {
  const parsed = new URL(url);
  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
    return { reason: "URL scheme must be http or https" };
  }
  try {
    const host = parsed.hostname.replace(/^\[|]$/g, "");
    const results = await Bun.dns.lookup(host);
    for (const { address } of results) {
      if (isBlockedAddress(address)) {
        return {
          reason: "URL must not resolve to a private or internal address",
        };
      }
    }
    const addresses = results.map(({ address }) => ({
      address,
      family:
        ipaddr.parse(address).kind() === "ipv6" ? (6 as const) : (4 as const),
    }));
    return { parsed, addresses };
  } catch {
    return { reason: `Could not resolve hostname: ${parsed.hostname}` };
  }
}
